// src/utils/usda.js
// All USDA FoodData Central API logic lives here.
// MealLog.jsx imports from this file — never calls fetch directly.
//
// API docs: https://api.nal.usda.gov/fdc/v1
// Key is stored in .env as REACT_APP_USDA_API_KEY.
// Results are trimmed before returning — raw USDA responses are never
// stored in Firestore or passed to components.

import logger from './logger';

const BASE_URL = 'https://api.nal.usda.gov/fdc/v1';
const API_KEY  = process.env.REACT_APP_USDA_API_KEY;

// ---------------------------------------------------------------------------
// searchFoods(query)
// Searches USDA for foods matching the query string.
// Returns an array of trimmed food objects ready for the UI, or [] on failure.
// Foods with no calorie data are filtered out before returning.
// ---------------------------------------------------------------------------
export async function searchFoods(query) {
  if (!query || query.trim().length < 2) return [];

  const params = new URLSearchParams({
    query:   query.trim(),
    api_key: API_KEY,
    pageSize: 20,
  });
  params.append('dataType', 'Foundation');
  params.append('dataType', 'SR Legacy');

  try {
    const res  = await fetch(`${BASE_URL}/foods/search?${params}`);
    if (!res.ok) throw new Error(`USDA API error: ${res.status}`);
    const data = await res.json();

    const results = (data.foods || [])
      .map(trimFood)
      .filter(f => f !== null);

    // Custom re-ranking — USDA's relevance ordering is coarse for multi-word
    // queries. Boost foods whose name starts with the query (score 0), then
    // foods that merely contain it (score 1), then everything else (score 2).
    // Within each bucket we preserve USDA's original order via index
    // tiebreaker so equally-ranked foods stay in their original sequence.
    if (!query || results.length === 0) return results;
    const q = query.trim().toLowerCase();
    return results
      .map((item, index) => {
        const name = (item.foodName || '').toLowerCase();
        let score;
        if (name.startsWith(q)) score = 0;
        else if (name.includes(q)) score = 1;
        else score = 2;
        return { item, index, score };
      })
      .sort((a, b) => a.score - b.score || a.index - b.index)
      .map(r => r.item);
  } catch (err) {
    logger.error(err, { page: 'usda', action: 'searchFoods' });
    return [];
  }
}

// ---------------------------------------------------------------------------
// trimFood(food)
// Extracts only what we need from a raw USDA food object.
// Returns null if no calorie data is available — caller must filter these out.
//
// Handles the two storage formats:
//   Foundation / SR Legacy — nutrients are per 100g.
//   Branded                — nutrients are per serving; servingSize is in grams.
//
// We normalize everything to kcalPer100g so serving math is consistent.
// ---------------------------------------------------------------------------
function trimFood(food) {
  const kcal = extractKcal(food);
  if (kcal === null) return null;

  const isBranded   = food.dataType === 'Branded';
  const servingGrams = isBranded && food.servingSize && food.servingSizeUnit === 'g'
    ? food.servingSize
    : null;

  // Branded foods store kcal per serving, not per 100g.
  // Convert to per-100g so buildServingOptions math is uniform.
  const kcalPer100g = isBranded && servingGrams
    ? (kcal / servingGrams) * 100
    : kcal;

  return {
    fdcId:        food.fdcId,
    foodName:     food.description,
    dataType:     food.dataType || 'Unknown',
    kcalPer100g:  Math.round(kcalPer100g),
    servingGrams, // non-null only for branded foods with gram serving size
  };
}

// ---------------------------------------------------------------------------
// extractKcal(food)
// Finds the Energy nutrient in kcal from a raw USDA food object.
// Returns the numeric value, or null if not found.
//
// USDA sometimes includes two Energy entries — one kcal, one kJ.
// We match on BOTH nutrientName and unitName to avoid returning kJ values,
// which would make calorie totals ~4x too high.
// ---------------------------------------------------------------------------
function extractKcal(food) {
  const nutrients = food.foodNutrients || [];

  const entry = nutrients.find(n =>
    (n.nutrientName === 'Energy' || n.name === 'Energy') &&
    (n.unitName === 'KCAL' || n.unit === 'KCAL')
  );

  return entry ? entry.value : null;
}

// ---------------------------------------------------------------------------
// Common serving sizes for well-known foods. Matched by food name regex.
// Prepended after the branded serving (if any) and before gram increments.
// ---------------------------------------------------------------------------
const COMMON_SERVINGS = [
  { pattern: /banana/i,          servings: [{ label: '1 small (101g)',  grams: 101 }, { label: '1 medium (118g)', grams: 118 }, { label: '1 large (136g)',  grams: 136 }] },
  { pattern: /orange/i,          servings: [{ label: '1 small (96g)',   grams: 96  }, { label: '1 medium (131g)', grams: 131 }, { label: '1 large (184g)',  grams: 184 }] },
  { pattern: /apple/i,           servings: [{ label: '1 small (149g)',  grams: 149 }, { label: '1 medium (182g)', grams: 182 }, { label: '1 large (223g)',  grams: 223 }] },
  { pattern: /egg/i,             servings: [{ label: '1 large (50g)',   grams: 50  }, { label: '2 large (100g)', grams: 100 }, { label: '3 large (150g)',  grams: 150 }] },
  { pattern: /chicken breast/i,  servings: [{ label: '3 oz (85g)',      grams: 85  }, { label: '4 oz (113g)',    grams: 113 }, { label: '6 oz (170g)',     grams: 170 }] },
  { pattern: /salmon|tilapia|cod|tuna|fish/i, servings: [{ label: '3 oz (85g)', grams: 85 }, { label: '4 oz (113g)', grams: 113 }, { label: '6 oz (170g)', grams: 170 }] },
  { pattern: /rice/i,            servings: [{ label: '1/2 cup cooked (100g)', grams: 100 }, { label: '1 cup cooked (200g)', grams: 200 }] },
  { pattern: /pasta|spaghetti|noodle/i, servings: [{ label: '1 cup cooked (140g)', grams: 140 }, { label: '2 cups cooked (280g)', grams: 280 }] },
  { pattern: /bread/i,           servings: [{ label: '1 slice (28g)',   grams: 28  }, { label: '2 slices (56g)', grams: 56  }] },
  { pattern: /potato/i,          servings: [{ label: '1 small (138g)',  grams: 138 }, { label: '1 medium (173g)', grams: 173 }, { label: '1 large (299g)', grams: 299 }] },
  { pattern: /broccoli|spinach|kale|lettuce/i, servings: [{ label: '1 cup (91g)', grams: 91 }, { label: '2 cups (182g)', grams: 182 }] },
  { pattern: /milk/i,            servings: [{ label: '1/2 cup (122g)',  grams: 122 }, { label: '1 cup (244g)',   grams: 244 }] },
  { pattern: /yogurt/i,          servings: [{ label: '1/2 cup (123g)',  grams: 123 }, { label: '1 cup (245g)',   grams: 245 }] },
  { pattern: /cheese/i,          servings: [{ label: '1 oz (28g)',      grams: 28  }, { label: '1.5 oz (43g)',   grams: 43  }] },
  { pattern: /oat|oatmeal/i,     servings: [{ label: '1/2 cup dry (40g)', grams: 40 }, { label: '1 cup cooked (234g)', grams: 234 }] },
  { pattern: /steak|beef|pork|ground/i, servings: [{ label: '3 oz (85g)', grams: 85 }, { label: '4 oz (113g)', grams: 113 }, { label: '6 oz (170g)', grams: 170 }] },
];

// ---------------------------------------------------------------------------
// buildServingOptions(trimmedFood)
// Returns an array of serving size options for the UI picker.
// Each option has: label (display string), grams (number), calories (number).
//
// Strategy (no second API call needed):
//   - For branded foods, prepend "1 serving (Xg)" as the default option.
//   - If food name matches COMMON_SERVINGS, include human-readable options.
//   - Always include standard gram increments as fallback.
// ---------------------------------------------------------------------------
export function buildServingOptions(food) {
  const options = [];

  // Branded foods get a "1 serving" option at the top
  if (food.servingGrams) {
    options.push({
      label:    `1 serving (${food.servingGrams}g)`,
      grams:    food.servingGrams,
      calories: Math.round((food.kcalPer100g * food.servingGrams) / 100),
    });
  }

  // Common serving sizes matched by food name
  const match = COMMON_SERVINGS.find(entry => entry.pattern.test(food.foodName));
  if (match) {
    for (const s of match.servings) {
      options.push({
        label:    s.label,
        grams:    s.grams,
        calories: Math.round((food.kcalPer100g * s.grams) / 100),
      });
    }
  }

  // Standard gram increments available for all foods
  const increments = [50, 100, 150, 200];
  for (const g of increments) {
    options.push({
      label:    `${g}g`,
      grams:    g,
      calories: Math.round((food.kcalPer100g * g) / 100),
    });
  }

  return options;
}